//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  DimensionValidationRules,
  VariationValidationRules,
} from '@campaigns/types/sampling/SamplingProductValidationTypes';
import { validateNumber } from '@campaigns/utils/FormUtils';
import { isDefined } from '@utils/filterPredicates';
import { URL_REGEX_PROTOCOL_ONLY } from '@utils/urlValidation';

export const URL_REGEX_RULE = new RegExp(URL_REGEX_PROTOCOL_ONLY);

export const SamplingProductValidationMessages = {
  FmvInvalidNumber: 'Please enter a valid number for the fair market value',
  FmvNegative: 'Fair market value must be greater than or equal to 0',
  ImageUnsupported: 'Invalid product image URL or unsupported format',
  InvalidDate: 'Please enter a valid date',
  InvalidProductId:
    'Product ID must not start with a space or dash, must end with a letter or number, and can only contain letters, numbers, underscores, periods, vertical bars, and dashes',
  InvalidUrl: 'Please enter a valid URL',
  MissingBrand: 'Please enter a product brand',
  MissingCategory: 'Please enter a product category',
  MissingDescription: 'Please enter a product description',
  MissingImageUrl: 'Please enter a product image URL',
  MissingName: 'Please enter a product name',
  MissingProductCode: 'Please enter a GTIN, UPC, EAN or PZN',
  MissingProductId: 'Please enter a product ID',
  QuantityInvalidNumber: 'Please enter a valid number for the quantity',
  QuantityTooLow: 'Quantity must be greater than 0',
  Required: 'Required',
  UploadImage: 'Please upload a product image',
} as const;

/**
 * Regular expression to validate product IDs.
 *
 * This regex ensures that:
 * - The string does not start with a hyphen (-).
 * - The string ends with a word character (alphanumeric), period (.), or hyphen (-).
 * - The string only contains word characters (alphanumeric), periods (.), hyphens (-), or pipe characters (|).
 *
 * Note: This version does not accept spaces anywhere in the string.
 *
 * Examples of valid IDs:
 * - "product-123"
 * - "item.name"
 * - "A1.B2-C3"
 * - "product|name"
 *
 * Examples of invalid IDs:
 * - " product123" (starts with a whitespace)
 * - "-product123" (starts with a hyphen)
 * - "product 123" (contains a whitespace)
 */
export const PRODUCT_ID_REGEX_RULE = /^(?=[^\-\s])(?=.*[\w.|-]$)[\w.|-]+$/;

/**
 * Validates that a product variant image URL has an http(s):// protocol
 */
export const isProductImageUrlValid = (url: string): boolean => {
  return URL_REGEX_RULE.test(url);
};

/**
 * Validates that the product image content type is supported and that it points to a
 * loadable image resource. The function attempts to load it using an Image element and
 * resolves based on the load result.
 * @param {string} url
 * @returns {Promise<boolean>}
 */
export const isProductImageContentTypeValid = async (
  url: string | undefined,
): Promise<boolean> => {
  if (!url) {
    return true;
  }

  // Use an img element instead of fetch to avoid CORS issues — browsers load images
  // cross-origin without requiring CORS headers, so this works even when CDNs reject
  // preflight requests or don't return Access-Control-Allow-Origin headers
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    const TIMEOUT_MS = 3000;

    function finish(isValid: boolean) {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(isValid);
    }

    const timer = setTimeout(() => finish(false), TIMEOUT_MS);

    img.onload = () => finish(true);
    img.onerror = () => finish(false);

    img.src = url;
  });
};

/**
 * Returns an error message when the quantity is not an integer of at least 1
 */
export const validateQuantity = (
  value: string | undefined,
): string | undefined => {
  if (
    !validateNumber(value) ||
    (value !== undefined && !Number.isInteger(Number(value)))
  ) {
    return SamplingProductValidationMessages.QuantityInvalidNumber;
  }
  if (value && Number(value) < 1) {
    return SamplingProductValidationMessages.QuantityTooLow;
  }
};

/**
 * Returns an error message when the fair market value is not a number of at least 0
 */
export const validateFairMarketValue = (
  value: string | undefined,
): string | undefined => {
  if (!validateNumber(value)) {
    return SamplingProductValidationMessages.FmvInvalidNumber;
  }
  if (value && Number(value) < 0) {
    return SamplingProductValidationMessages.FmvNegative;
  }
};

export const getVariationsValidationRules = (
  index: number,
  variationsLength: number,
): VariationValidationRules => {
  return {
    [`variations.${index}.fairMarketValue`]: {
      required: {
        value: true,
        message: SamplingProductValidationMessages.Required,
      },
      validate: validateFairMarketValue,
    },
    [`variations.${index}.imgUrl`]: {
      validate: {
        validUrl: (value: string | undefined) =>
          // An empty value is allowed here; the `required` rule below reports it when needed.
          // A blob URL means a file was selected for upload; it's uploaded separately
          // via uploadImageFilesFromProduct and doesn't need to match the http(s) URL format.
          !value ||
          value.startsWith('blob:') ||
          isProductImageUrlValid(value) ||
          SamplingProductValidationMessages.InvalidUrl,
        validContentType: async (value: string | undefined) => {
          // Blob URLs still get checked here so a stale/revoked object URL
          // (no backing file) can't slip through and reach the gateway.
          const isValid = await isProductImageContentTypeValid(value);
          return isValid || SamplingProductValidationMessages.ImageUnsupported;
        },
      },
      required: {
        value: variationsLength > 1,
        message: SamplingProductValidationMessages.Required,
      },
    },
    [`variations.${index}.logisticsRef`]: {
      required: {
        value: true,
        message: SamplingProductValidationMessages.Required,
      },
    },
    [`variations.${index}.quantity`]: {
      required: {
        value: true,
        message: SamplingProductValidationMessages.Required,
      },
      validate: validateQuantity,
    },
    [`variations.${index}.variantName`]: {
      required: {
        value: variationsLength > 1,
        message: SamplingProductValidationMessages.Required,
      },
    },
  };
};

export const getProductDimensionsValidationRules = (
  number: number,
): DimensionValidationRules => {
  return {
    [`products.${number}.dimensions.format`]: {
      required: {
        value: true,
        message: 'Please select a dimension format',
      },
    },
    [`products.${number}.dimensions.depth`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the depth';
        }
        if (value && value < 0) {
          return 'Depth must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.height`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the height';
        }
        if (value && value < 0) {
          return 'Height must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.width`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the width';
        }
        if (value && value < 0) {
          return 'Width must be greater than or equal to 0';
        }
      },
    },
  };
};
